import styles from "./FilterTabs.module.css";
import { useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { IconContacts } from "shared/icons/IconContacts";
import { FILTER_TYPE } from "shared/enums";
import { useFreshState } from "shared/hooks/useFreshState";
import { FilterHeader } from "./FilterHeader/FilterHeader";

/**
 * Header with filtering functionality for contact statuses.
 */
export function FilterTabs({ selected, onSelect, totalRecords }) {
  const { t } = useTranslation();
  const [_selected, _setSelected] = useFreshState(selected);

  const _onSelect = useCallback(
    (value) => {
      _setSelected(value);
      onSelect(value);
    },
    [_setSelected, onSelect]
  );

  return (
    <nav className={styles.container}>
      <ul className={styles.tabs}>
        <li>
          <button
            className={classNames(styles.tab, styles.customersTab, {
              [styles.selected]: _selected === FILTER_TYPE.Customer,
            })}
            onClick={(e) => {
              e.preventDefault();

              if (_selected === FILTER_TYPE.Customer) {
                _onSelect(null);
              } else {
                _onSelect(FILTER_TYPE.Customer);
              }
            }}
          >
            {t("labels.tabCustomers")}
          </button>
        </li>
        <li>
          <button
            className={classNames(styles.tab, styles.prospectsTab, {
              [styles.selected]: _selected === FILTER_TYPE.Prospect,
            })}
            onClick={(e) => {
              e.preventDefault();

              if (_selected === FILTER_TYPE.Prospect) {
                _onSelect(null);
              } else {
                _onSelect(FILTER_TYPE.Prospect);
              }
            }}
          >
            {t("labels.tabProspects")}
          </button>
        </li>
        <li>
          <button
            className={classNames(styles.tab, styles.ibosTab, {
              [styles.selected]: _selected === FILTER_TYPE.IBO,
            })}
            onClick={(e) => {
              e.preventDefault();

              if (_selected === FILTER_TYPE.IBO) {
                _onSelect(null);
              } else {
                _onSelect(FILTER_TYPE.IBO);
              }
            }}
          >
            {t("labels.tabIBOs")}
          </button>
        </li>
      </ul>

      <div className={styles.statusHeader}>
        {_selected == null && (
          <FilterHeader
            icon={<IconContacts width={24} height={24} />}
            title={t("labels.tabContacts")}
            count={totalRecords}
          />
        )}

        {_selected === FILTER_TYPE.Customer && (
          <FilterHeader
            icon={<IconContacts width={24} height={24} />}
            title={t("labels.tabCustomers")}
            count={totalRecords}
          />
        )}

        {_selected === FILTER_TYPE.Prospect && (
          <FilterHeader
            icon={<IconContacts width={24} height={24} />}
            title={t("labels.tabProspects")}
            count={totalRecords}
          />
        )}

        {_selected === FILTER_TYPE.IBO && (
          <FilterHeader
            icon={<IconContacts width={24} height={24} />}
            title={t("labels.tabIBOs")}
            count={totalRecords}
          />
        )}
      </div>
    </nav>
  );
}

FilterTabs.propTypes = {
  /**
   * The value for the currently selected tab.
   */
  selected: PropTypes.oneOf([
    FILTER_TYPE.Prospect,
    FILTER_TYPE.Customer,
    FILTER_TYPE.IBO,
  ]),

  /**
   * Callback function that receives newly selected values when
   * the user clicks a tab.
   */
  onSelect: PropTypes.func.isRequired,

  /**
   * A number of total records matching the selected type.
   */
  totalRecords: PropTypes.number.isRequired,
};
